export type CalendarMoveMode = "today" | "week";

export interface CalendarMoveTarget {
    mode: CalendarMoveMode;
    dateKey: string;
    weekStartKey: string;
    weekEndKey: string;
    weekDateKeys: string[];
    visibleWeekDateKeys: string[];
    scrollDateKey: string;
    found: boolean;
    exact: boolean;
    fallback: boolean;
    reason: string;
}

const KOREA_TIMEZONE_OFFSET_MINUTES = 9 * 60;
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function getKoreaDateKey(value: Date = new Date()) {
    const source = value instanceof Date && !Number.isNaN(value.getTime()) ? value : new Date();
    const koreaTime = new Date(source.getTime() + KOREA_TIMEZONE_OFFSET_MINUTES * 60 * 1000);
    return formatUtcDateKey(koreaTime);
}

export function getKoreaWeekStartKey(dateKey: string = getKoreaDateKey()) {
    const key = isCalendarDateKey(dateKey) ? dateKey : getKoreaDateKey();
    const date = parseDateKeyAsUtcDate(key);
    if (!date) return "";

    const weekday = date.getUTCDay();
    const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
    return addDaysToDateKey(key, -daysFromMonday);
}

export function getKoreaWeekEndKey(dateKey: string = getKoreaDateKey()) {
    const weekStartKey = getKoreaWeekStartKey(dateKey);
    return weekStartKey ? addDaysToDateKey(weekStartKey, 6) : "";
}

export function getKoreaWeekDateKeys(dateKey: string = getKoreaDateKey()) {
    const weekStartKey = getKoreaWeekStartKey(dateKey);
    if (!weekStartKey) return [];
    return Array.from({ length: 7 }, (_, index) => addDaysToDateKey(weekStartKey, index));
}

export function isDateKeyInKoreaWeek(dateKey: string, weekStartKey: string) {
    if (!isCalendarDateKey(dateKey) || !isCalendarDateKey(weekStartKey)) return false;
    const weekEndKey = addDaysToDateKey(weekStartKey, 6);
    return dateKey >= weekStartKey && dateKey <= weekEndKey;
}

export function addDaysToDateKey(dateKey: string, amount: number) {
    const date = parseDateKeyAsUtcDate(dateKey);
    if (!date) return "";
    date.setUTCDate(date.getUTCDate() + Math.trunc(Number(amount) || 0));
    return formatUtcDateKey(date);
}

export function isCalendarDateKey(value: any) {
    if (typeof value !== "string" || !DATE_KEY_PATTERN.test(value)) return false;
    const date = parseDateKeyAsUtcDate(value);
    return !!date && formatUtcDateKey(date) === value;
}

export function resolveCalendarMoveTarget(
    rows: any[] = [],
    visibleRows: any[] = [],
    targetDateKey: string = getKoreaDateKey(),
    mode: CalendarMoveMode = "today"
): CalendarMoveTarget {
    const dateKey = isCalendarDateKey(targetDateKey) ? targetDateKey : getKoreaDateKey();
    const weekStartKey = getKoreaWeekStartKey(dateKey);
    const weekEndKey = getKoreaWeekEndKey(weekStartKey);
    const weekDateKeys = getKoreaWeekDateKeys(dateKey);
    const allDateSet = collectRowDateSet(rows);
    const visibleDateSet = collectRowDateSet(visibleRows);
    const visibleWeekDateKeys = weekDateKeys.filter(key => visibleDateSet[key] === true);
    const hasDate = allDateSet[dateKey] === true;
    const hasWeek = weekDateKeys.some(key => allDateSet[key] === true);

    let scrollDateKey = "";
    let reason = "";

    if (mode === "week") {
        if (!hasWeek) {
            reason = "missing-week";
        } else if (visibleWeekDateKeys.length > 0) {
            scrollDateKey = visibleWeekDateKeys[0];
            reason = "visible-week";
        } else {
            reason = "hidden-week";
        }
    } else if (!hasDate) {
        reason = "missing-date";
    } else if (visibleDateSet[dateKey] === true) {
        scrollDateKey = dateKey;
        reason = "visible-date";
    } else if (visibleWeekDateKeys.length > 0) {
        scrollDateKey = visibleWeekDateKeys[0];
        reason = "hidden-date-visible-week";
    } else {
        reason = "hidden-date";
    }

    return {
        mode,
        dateKey,
        weekStartKey,
        weekEndKey,
        weekDateKeys,
        visibleWeekDateKeys,
        scrollDateKey,
        found: !!scrollDateKey,
        exact: !!scrollDateKey && scrollDateKey === dateKey,
        fallback: !!scrollDateKey && scrollDateKey !== dateKey,
        reason
    };
}

function collectRowDateSet(rows: any[]) {
    const set: any = {};
    if (!Array.isArray(rows)) return set;
    rows.forEach(row => {
        const dateKey = row && isCalendarDateKey(row.date) ? row.date : "";
        if (dateKey) set[dateKey] = true;
    });
    return set;
}

function parseDateKeyAsUtcDate(dateKey: string) {
    if (typeof dateKey !== "string" || !DATE_KEY_PATTERN.test(dateKey)) return null;
    const parts = dateKey.split("-").map(part => Number(part));
    const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    if (Number.isNaN(date.getTime())) return null;
    return date;
}

function formatUtcDateKey(date: Date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
